import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Edit, 
  Package, 
  User, 
  Phone, 
  MapPin, 
  Wallet, 
  FileText,
  CheckCircle2,
  ShoppingCart,
  Printer,
  Download
} from 'lucide-react';
import {useNavigate} from 'react-router-dom';

export default function OrderInvoice() {
  const [orderData] = useState({
    orderId: 'ORD/5',
    status: 'Accepted',
    poReference: '-',
    source: 'In-Store',
    created: '06-Nov-2025 15:42',
    updated: '06-Nov-2025 15:42',
    customer: {
      name: 'Sample Customer',
      contact: '9999888877',
      balance: 359.00
    },
    patient: {
      name: 'Sample Customer',
      relation: 'Self',
      contact: '9999888877'
    },
    fulfillment: 'Pickup',
    billingAddress: 'Sector 37, Gurgaon, Haryana',
    payment: {
      status: 'Paid',
      mode: 'Cash on Hand',
      txnUtr: '-',
      remarks: '-'
    },
    products: [
      {
        id: 1,
        name: 'DAB TAB',
        manufacturer: 'MOREPEN LABORATORIES LTD (DR ...)',
        orderQty: 1,
        unit: 'packs',
        stockQty: 81,
        stockUnit: 'packs',
        unitsPerPack: 42,
        status: 'Adequate',
        mrp: 4.70,
        rate: 4.70,
        disc: '-',
        gst: 5,
        amount: 4.70
      }
    ],
    summary: {
      products: 1,
      taxableAmt: 4.48,
      cgstSgst: 0.22,
      deliveryFee: 0,
      totalAmount: 5,
      amountPaid: 5.00,
      payableAmount: 0
    }
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-9xl mx-auto">
        {/* Header - Reduced padding */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => window.history.back()}>
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-semibold text-gray-900">{orderData.orderId}</h1>
                  <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-xs font-medium">
                    {orderData.status}
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-0.5">Verify & Update Order Detail</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5 text-xs font-medium shadow-sm">
                <ShoppingCart className="w-3.5 h-3.5" />
                Create Sale Bill (F2)
              </button>
              <button className="px-3 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-1.5 text-xs font-medium shadow-sm">
                <Edit className="w-3.5 h-3.5" />
                Edit Order (F4)
              </button>
              <button className="p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
                <Printer className="w-4 h-4 text-gray-600" />
              </button>
              <button className="p-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">
                <Download className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Left Column - Reduced spacing between cards */}
          <div className="space-y-3">
            {/* Order Info Card */}
            <div className="bg-white rounded-lg shadow-md p-3.5 border border-gray-200">
              <div className="flex items-center gap-1.5 mb-3">
                <FileText className="w-4 h-4 text-emerald-600" />
                <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Order Information</h2>
              </div>
              <div className="space-y-2">
                {[
                  { label: 'PO Reference', value: orderData.poReference },
                  { label: 'Source', value: orderData.source },
                  { label: 'Created', value: orderData.created },
                  { label: 'Updated', value: orderData.updated }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{item.label}</span>
                    <span className="text-xs text-gray-800 font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Card */}
            <div className="bg-white rounded-lg shadow-md p-3.5 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4 text-emerald-600" />
                  <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Customer</h2>
                </div>
                <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                  <Edit className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Name</span>
                  <span className="text-xs text-gray-800 font-medium">{orderData.customer.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Contact</span>
                  <span className="text-xs text-gray-800 font-medium">{orderData.customer.contact}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-xs text-gray-500">Balance</span>
                  <span className="text-sm font-semibold text-emerald-600">₹{orderData.customer.balance.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Patient Card */}
            <div className="bg-white rounded-lg shadow-md p-3.5 border border-gray-200">
              <div className="flex items-center gap-1.5 mb-3">
                <User className="w-4 h-4 text-emerald-600" />
                <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Patient</h2>
              </div>
              <div className="space-y-2">
                {[
                  { label: 'Name', value: orderData.patient.name },
                  { label: 'Relation', value: orderData.patient.relation },
                  { label: 'Contact', value: orderData.patient.contact }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{item.label}</span>
                    <span className="text-xs text-gray-800 font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fulfillment */}
            <div className="bg-white rounded-lg shadow-md p-3.5 border border-gray-200">
              <div className="flex items-center gap-1.5 mb-2">
                <Package className="w-4 h-4 text-emerald-600" />
                <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Fulfillment</h2>
              </div>
              <p className="text-xs text-gray-800 font-medium">{orderData.fulfillment}</p>
            </div>

            {/* Billing Address */}
            <div className="bg-white rounded-lg shadow-md p-3.5 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Billing Address</h2>
                </div>
                <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                  <Edit className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{orderData.billingAddress}</p>
            </div>

            {/* Payment Card */}
            <div className="bg-white rounded-lg shadow-md p-3.5 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <Wallet className="w-4 h-4 text-emerald-600" />
                  <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Payment Detail</h2>
                </div>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                  {orderData.payment.status}
                </span>
              </div>
              <div className="space-y-2">
                {[
                  { label: 'Mode', value: orderData.payment.mode },
                  { label: 'Txn UTR', value: orderData.payment.txnUtr },
                  { label: 'Txn Remarks', value: orderData.payment.remarks }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{item.label}</span>
                    <span className="text-xs text-gray-800 font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Products */}
          <div className="col-span-2">
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1.5">
                  <Package className="w-4 h-4 text-emerald-600" />
                  <h2 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Products</h2>
                </div>
                <button className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-xs font-medium shadow-sm">
                  Product Actions
                </button>
              </div>

              {/* Products Table - Tighter spacing */}
              <div className="overflow-x-auto bg-gray-50 rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Product</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600 uppercase">Order Qty</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600 uppercase">Stock Qty</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-gray-600 uppercase">MRP</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-gray-600 uppercase">Rate</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600 uppercase">Disc</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold text-gray-600 uppercase">GST</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {orderData.products.map((product) => (
                      <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-3">
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-1.5 text-xs">
                              {product.name}
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{product.manufacturer}</div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <div className="text-xs text-gray-800 font-medium">{product.orderQty} {product.unit}</div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <div className="text-xs text-gray-800 font-medium">{product.stockQty} {product.stockUnit}</div>
                          <div className="text-xs text-gray-500">{product.unitsPerPack} units</div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                            {product.status}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right text-xs text-gray-800 font-medium">₹{product.mrp.toFixed(2)}</td>
                        <td className="py-3 px-2 text-right text-xs text-gray-800 font-medium">₹{product.rate.toFixed(2)}</td>
                        <td className="py-3 px-2 text-center text-xs text-gray-500">{product.disc}</td>
                        <td className="py-3 px-2 text-center text-xs text-gray-800 font-medium">{product.gst}%</td>
                        <td className="py-3 px-3 text-right text-xs font-semibold text-gray-900">₹{product.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary Section - Compact & Highlighted */}
              <div className="mt-4 pt-4 border-t-2 border-gray-200">
                <div className="flex justify-end">
                  <div className="w-80 space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Products</span>
                      <span className="text-xs font-semibold text-gray-800">{orderData.summary.products}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Taxable Amount</span>
                      <span className="text-xs font-semibold text-gray-800">₹{orderData.summary.taxableAmt.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">CGST + SGST</span>
                      <span className="text-xs font-semibold text-gray-800">₹{orderData.summary.cgstSgst.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Delivery Fee</span>
                      <span className="text-xs font-semibold text-gray-800">-</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                      <span className="text-xs font-semibold text-gray-800">Total Amount</span>
                      <span className="text-base font-bold text-gray-900">₹{orderData.summary.totalAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-600">Amount Paid</span>
                      <span className="text-xs font-semibold text-emerald-600">₹{orderData.summary.amountPaid.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-emerald-100 -mx-3 px-3 py-2.5 rounded-b-lg mt-2">
                      <span className="text-xs font-bold text-emerald-900">Payable Amount</span>
                      <span className="text-lg font-bold text-emerald-700">₹{orderData.summary.payableAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Keyboard Shortcuts - Compact */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                  <span>Create Bill: <strong className="text-emerald-600">F2</strong></span>
                  <span className="text-gray-300">|</span>
                  <span>Edit Order: <strong className="text-emerald-600">F4</strong></span>
                  <span className="text-gray-300">|</span>
                  <span>Go Back: <strong className="text-emerald-600">ESC</strong></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
